import { html } from 'lit';
import { cache } from 'lit/directives/cache.js';
import { until } from 'lit/directives/until.js';
import { view } from '../factory.js';
import { useMemo, useState } from '../hooks/index.js';
import { View } from '../view.js';
import { parseRoute, RoutingParam } from './route-parser.js';
import { useLocation } from './use-location.js';

function template({ routes, fallback, manualLocation }: RouterProps) {
  const [location, setLocation] = useState<Location | undefined>(
    manualLocation // for testing
  );

  useLocation(loc => {
    if (!manualLocation) {
      setLocation({ ...loc });
    }
  });

  const [route, routingParam] = useMemo(() => {
    if (!location) {
      return [undefined, undefined];
    }
    const { pathname: path, search } = location;

    let rParam: RoutingParam | undefined;

    const r = routes
      .filter(({ pattern }) => !!pattern)
      .find(({ pattern }) => {
        const parsingResult = parseRoute(pattern!, path, search);
        if (parsingResult) {
          rParam = parsingResult;
          return true;
        }
        return false;
      });

    return [r, rParam];
  }, [routes, location]);

  function resolveRoute(route: Route) {
    return html`
      ${until(
        Promise.resolve(route.viewFactory({ routingParam, active: true })).then(
          v => v.body
        )
      )}
    `;
  }

  function display() {
    if (!route) {
      return fallback ? resolveRoute(fallback) : html``;
    }
    return resolveRoute(route);
  }

  return html`${cache(display())}`;
}

export class RouterProps {
  routes: Route[] = [];
  fallback?: Route = undefined;
  manualLocation?: Location = undefined;
}

export interface Route {
  pattern?: string;
  viewFactory: <T, U>(param: {
    routingParam?: RoutingParam<T, U>;
    active: boolean;
  }) => Promise<View> | View;
}

export const [Router, RouterView] = view('jsview-router', {
  template,
  Props: RouterProps,
  mapBuilder: RouterBuilder => (...routes: Route[]) =>
    RouterBuilder().routes(routes),
});

export * from './link.js';
export { navigate } from './use-location.js';
